import * as N3 from "n3";
import { DataFactory } from "n3";
import { v4 as uuidv4 } from "uuid";
import { DEFAULT_CONTROLLED_VOCABULARY } from "./concepts/controlled-vocabulary.js";
const IRI = DataFactory.namedNode;
const Literal = DataFactory.literal;
/** Language of a dct:title that carries no language tag. */
const DEFAULT_TITLE_LANGUAGE = "en";
const RDF_PREFIX = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";
const RDF = {
    "type": IRI(RDF_PREFIX + "type"),
};
const DCT_PREFIX = "http://purl.org/dc/terms/";
const DCT = {
    "title": IRI(DCT_PREFIX + "title"),
    "references": IRI(DCT_PREFIX + "references"),
    "conformsTo": IRI(DCT_PREFIX + "conformsTo"),
};
const SKOS_CORE = IRI("http://www.w3.org/2004/02/skos/core");
const DCAT_PREFIX = "http://www.w3.org/ns/dcat#";
const DCAT = {
    "Catalog": IRI(DCAT_PREFIX + "Catalog"),
    "Dataset": IRI(DCAT_PREFIX + "Dataset"),
    "Distribution": IRI(DCAT_PREFIX + "Distribution"),
    "dataset": IRI(DCAT_PREFIX + "dataset"),
    "distribution": IRI(DCAT_PREFIX + "distribution"),
    "downloadURL": IRI(DCAT_PREFIX + "downloadURL"),
    "accessURL": IRI(DCAT_PREFIX + "accessURL"),
    "landingPage": IRI(DCAT_PREFIX + "landingPage"),
};
const SHACL_PREFIX = "http://www.w3.org/ns/shacl#";
const SHACL = {
    "pattern": IRI(SHACL_PREFIX + "pattern"),
};
/**
 * Mints the IRI of the DCAT catalog of controlled vocabularies owned by a
 * given package, within a project whose specifications are published under
 * `baseIri`.
 */
export function controlledVocabularyCatalogIri(baseIri, packageId) {
    return `${baseIri}controlled-vocabulary-catalog/${encodeURIComponent(packageId)}`;
}
/**
 * Mints a stable IRI for a vocabulary's DCAT dataset record within a given
 * catalog. Used as a fallback when the vocabulary has no `iri` of its own
 * yet (i.e. it has never been exported/imported before).
 */
export function controlledVocabularyDatasetIri(catalogIri, vocabularyId) {
    return `${catalogIri}/dataset/${encodeURIComponent(vocabularyId)}`;
}
/**
 * Writes controlled vocabulary models as DCAT datasets of the given DCAT catalog
 * uses an existing writer - does not finalize the writer, so callers can merge this
 * into a larger document (e.g. dsv.ttl) before serializing.
 */
export function writeControlledVocabularyCatalogQuads(writer, catalogIri, vocabularies) {
    writer.addQuad(IRI(catalogIri), RDF.type, DCAT.Catalog);
    for (const vocabulary of vocabularies) {
        const datasetIri = IRI(vocabulary.iri ?? controlledVocabularyDatasetIri(catalogIri, vocabulary.id));
        writer.addQuad(IRI(catalogIri), DCAT.dataset, datasetIri);
        writer.addQuad(datasetIri, RDF.type, DCAT.Dataset);
        for (const [language, title] of Object.entries(vocabulary.title)) {
            if (title) {
                writer.addQuad(datasetIri, DCT.title, Literal(title, language));
            }
        }
        if (vocabulary.references) {
            // Links this metadata record to the vocabulary it describes
            // - its skos:ConceptScheme when the vocabulary conforms to SKOS,
            // otherwise its main download or documentation URL.
            writer.addQuad(datasetIri, DCT.references, IRI(vocabulary.references));
        }
        if (vocabulary.conformsToSkos) {
            writer.addQuad(datasetIri, DCT.conformsTo, SKOS_CORE);
        }
        if (vocabulary.pattern) {
            writer.addQuad(datasetIri, SHACL.pattern, Literal(vocabulary.pattern));
        }
        if (vocabulary.documentation) {
            writer.addQuad(datasetIri, DCAT.landingPage, IRI(vocabulary.documentation));
        }
        const distributionQuads = [{ predicate: RDF.type, object: DCAT.Distribution }];
        if (vocabulary.distribution.downloadUrl) {
            distributionQuads.push({ predicate: DCAT.downloadURL, object: IRI(vocabulary.distribution.downloadUrl) });
        }
        if (vocabulary.distribution.accessUrl) {
            distributionQuads.push({ predicate: DCAT.accessURL, object: IRI(vocabulary.distribution.accessUrl) });
        }
        writer.addQuad(datasetIri, DCAT.distribution, writer.blank(distributionQuads));
    }
}
/**
 * Serializes a project's controlled vocabularies as a standalone DCAT
 * catalog in Turtle. Each vocabulary becomes a dcat:Dataset - metadata entry
 * about the CV. dct:references points to the CV's skos:ConceptScheme,
 * or to its main download/documentation URL when the CV does not conform to SKOS.
 * dct:conformsTo skos marks the CVs that do conform to SKOS.
 */
export function controlledVocabulariesToDcatCatalog(catalogIri, vocabularies) {
    const writer = new N3.Writer({
        prefixes: {
            rdf: RDF_PREFIX,
            dct: DCT_PREFIX,
            dcat: DCAT_PREFIX,
            sh: SHACL_PREFIX,
        },
    });
    writeControlledVocabularyCatalogQuads(writer, catalogIri, vocabularies);
    return new Promise((resolve, reject) => {
        writer.end((error, result) => {
            if (error) {
                reject(error);
            }
            else {
                resolve(result);
            }
        });
    });
}
/**
 * Parses dcat:Dataset records (and their dcat:Distribution blank nodes) out
 * of a set of quads into ControlledVocabulary entities - the mirror image of
 * writeControlledVocabularyCatalogQuads. Each returned vocabulary gets a
 * fresh local id (unrelated to the source project's own) and its `iri` set
 * to the dataset's own subject IRI from the source RDF, so a re-export can
 * reuse it instead of minting a new one.
 */
export function parseControlledVocabularyCatalog(quads) {
    const store = new N3.Store(quads);
    const result = [];
    for (const datasetSubject of store.getSubjects(RDF.type, DCAT.Dataset, null)) {
        const title = {};
        for (const object of store.getObjects(datasetSubject, DCT.title, null)) {
            const language = object.language || DEFAULT_TITLE_LANGUAGE;
            title[language] ??= object.value;
        }
        const references = store.getObjects(datasetSubject, DCT.references, null)[0]?.value ?? "";
        const conformsToSkos = store.countQuads(datasetSubject, DCT.conformsTo, SKOS_CORE, null) > 0;
        const documentation = store.getObjects(datasetSubject, DCAT.landingPage, null)[0]?.value ?? null;
        const pattern = store.getObjects(datasetSubject, SHACL.pattern, null)[0]?.value ?? null;
        const distributionNode = store.getObjects(datasetSubject, DCAT.distribution, null)[0];
        const downloadUrl = distributionNode ? (store.getObjects(distributionNode, DCAT.downloadURL, null)[0]?.value ?? null) : null;
        // The access URL is required, a file that can be downloaded can be accessed there.
        const accessUrl = distributionNode ? (store.getObjects(distributionNode, DCAT.accessURL, null)[0]?.value ?? downloadUrl ?? "") : "";
        result.push({
            ...DEFAULT_CONTROLLED_VOCABULARY,
            id: uuidv4(),
            iri: datasetSubject.value,
            title,
            references,
            conformsToSkos,
            documentation,
            pattern,
            distribution: { downloadUrl, accessUrl },
        });
    }
    return result;
}
//# sourceMappingURL=dcat-rdf.js.map